import { useMutation, useQueryClient } from '@tanstack/react-query';
import pluralize from 'pluralize';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';

import { getCommitmentsAssignmentsListQueryKey } from 'hooks/queries/commitments/useAssignmentsQuery';
import { getCommitmentAssignmentsQueryKey } from 'hooks/queries/commitments/useCommitmentAssignmentsQuery';
import { getCommitmentsQueryKey } from 'hooks/queries/commitments/useCommitmentsQuery';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

export const useDeleteCommitmentsMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganizations();

  return useMutation({
    mutationFn: async (commitmentIds: string[]) => {
      if (commitmentIds.length > 1) {
        return await apiClient.commitments.deleteCommitments({
          organizationId: currentOrganization!.id,
          body: { commitmentIds },
        });
      }
      return await apiClient.commitments.deleteCommitment({
        commitmentId: commitmentIds[0],
      });
    },
    onSuccess: (_, commitmentIds) => {
      onSuccess?.();
      queryClient.invalidateQueries({
        queryKey: getCommitmentsQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getCommitmentsAssignmentsListQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getCommitmentAssignmentsQueryKey(),
      });
      notify.success(
        `${pluralize('Commitment', commitmentIds.length)} deleted`
      );
    },
    onError: (error, commitmentIds) => {
      notify.error(
        `Failed to delete ${pluralize(
          'commitment',
          commitmentIds.length
        )}: ${parseApiError(error)}`
      );
    },
  });
};
